import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Button,
  Table,
  message,
  Spin,
  Card,
  Row,
  Col,
  Typography,
  Space,
  Input,
  Modal,
  Form,
  Empty,
  Popconfirm,
  Select,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import "../../css/sm-art-common.css";
import CriticalOperationService from "../../services/CriticalOperationService";

const { Title, Text } = Typography;
const { Option } = Select;

const CriticalOperationMaster = () => {
  const [operations, setOperations] = useState([]);
  const [filteredOperations, setFilteredOperations] = useState([]);
  const [lines, setLines] = useState([]);
  const [devices, setDevices] = useState([]);
  const [tlsOperations, setTlsOperations] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mastersLoading, setMastersLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  const [form] = Form.useForm();

  const dropdownSpinner = (
    <div style={{ textAlign: "center", padding: 8 }}>
      <Spin size="small" />
    </div>
  );

  const loadMasters = useCallback(async () => {
    try {
      setMastersLoading(true);

      const [lineRes, deviceRes, opRes, staffRes] = await Promise.all([
        CriticalOperationService.getLines(),
        CriticalOperationService.listCriticalDevices(),
        CriticalOperationService.getOperations(),
        CriticalOperationService.listStaffUsersDept3(),
      ]);

      const lineList =
        lineRes?.ok && lineRes?.data?.status
          ? lineRes?.data?.line_no || []
          : [];

      let deviceList = [];
      if (deviceRes?.ok && deviceRes?.data?.status) {
        if (Array.isArray(deviceRes?.data?.data)) {
          deviceList = deviceRes.data.data;
        } else if (Array.isArray(deviceRes?.data?.devices)) {
          deviceList = deviceRes.data.devices;
        } else if (Array.isArray(deviceRes?.data?.result)) {
          deviceList = deviceRes.data.result;
        }
      }

      const opList =
        opRes?.ok && opRes?.data?.status ? opRes?.data?.data || [] : [];

      const staffList =
        staffRes?.ok && staffRes?.data?.status ? staffRes?.data?.data || [] : [];

      setLines(Array.isArray(lineList) ? lineList : []);
      setDevices(Array.isArray(deviceList) ? deviceList : []);
      setTlsOperations(Array.isArray(opList) ? opList : []);
      setStaffUsers(Array.isArray(staffList) ? staffList : []);
    } catch (error) {
      console.error(error);
      setLines([]);
      setDevices([]);
      setTlsOperations([]);
      setStaffUsers([]);
    } finally {
      setMastersLoading(false);
    }
  }, []);

  const loadOperations = useCallback(async () => {
    setLoading(true);

    try {
      const res = await CriticalOperationService.getCriticalOperations();
      console.log("Critical operations API:", res);

      if (res?.ok && res?.data?.status) {
        let rows = [];

        if (Array.isArray(res?.data?.data)) {
          rows = res.data.data;
        } else if (Array.isArray(res?.data?.master_operations)) {
          rows = res.data.master_operations;
        } else if (Array.isArray(res?.data?.result)) {
          rows = res.data.result;
        }

        const normalized = rows.map((item, index) => ({
          ...item,
          key:
            item.id ||
            item.com_id ||
            item.master_operation_id ||
            item.operation_id ||
            index + 1,
          id:
            item.id ||
            item.com_id ||
            item.master_operation_id ||
            item.operation_id ||
            index + 1,
          line_no: item.line_no || item.line || "",
          op_id: item.op_id || item.operation_id || "",
          op_name: item.op_name || item.operation || "",
          device_id: item.device_id || "",
          target_pcs: item.target_pcs || "",
          count_pcs: item.count_pcs || item.count || "",
          operator_id: item.operator_id || item.su_id || "",
          operator_name:
            item.full_name?.trim() ||
            item.operator_name?.trim() ||
            item.operator?.trim() ||
            item.username?.trim() ||
            "Not Assigned",
          order_no: item.order_no || "",
          status: item.status || "active",
        }));

        setOperations(normalized);
        setFilteredOperations(normalized);
      } else {
        setOperations([]);
        setFilteredOperations([]);
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to load critical operations");
      setOperations([]);
      setFilteredOperations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMasters();
    loadOperations();
  }, [loadMasters, loadOperations]);

  useEffect(() => {
    if (!searchText.trim()) {
      setFilteredOperations(operations);
      return;
    }

    const q = searchText.toLowerCase();

    const filtered = operations.filter(
      (item) =>
        String(item.id || "").toLowerCase().includes(q) ||
        String(item.line_no || "").toLowerCase().includes(q) ||
        String(item.op_name || "").toLowerCase().includes(q) ||
        String(item.device_id || "").toLowerCase().includes(q) ||
        String(item.target_pcs || "").toLowerCase().includes(q) ||
        String(item.count_pcs || "").toLowerCase().includes(q) ||
        String(item.operator_name || "").toLowerCase().includes(q) ||
        String(item.order_no || "").toLowerCase().includes(q) ||
        String(item.status || "").toLowerCase().includes(q)
    );

    setFilteredOperations(filtered);
  }, [searchText, operations]);

  const handleOpenAdd = async () => {
    setEditingRow(null);
    form.resetFields();
    setOpenModal(true);

    await loadMasters();

    form.setFieldsValue({
      count_pcs: 1,
      order_no: 1,
      status: "active",
    });
  };

  const handleOpenEdit = async (record) => {
    setEditingRow(record);
    setOpenModal(true);

    await loadMasters();

    form.setFieldsValue({
      line_no: record.line_no || undefined,
      op_id: record.op_id || undefined,
      device_id: record.device_id || undefined,
      target_pcs: record.target_pcs,
      count_pcs: record.count_pcs ? Number(record.count_pcs) : undefined,
      operator_id: record.operator_id || undefined,
      order_no: record.order_no ? Number(record.order_no) : undefined,
      status: record.status || "active",
    });
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingRow(null);
    form.resetFields();
  };

  const handleSave = async (values) => {
    try {
      setSaving(true);

      let res;
      if (editingRow) {
        res = await CriticalOperationService.updateCriticalOperation(
          editingRow.id,
          values
        );
      } else {
        res = await CriticalOperationService.addCriticalOperation(values);
      }

      if (res?.ok && res?.data?.status) {
        message.success(
          editingRow
            ? "Critical operation updated successfully"
            : "Critical operation added successfully"
        );
        handleCloseModal();
        loadOperations();
      } else {
        message.error(res?.data?.message || "Failed");
      }
    } catch (error) {
      console.error(error);
      message.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      const res = await CriticalOperationService.deleteCriticalOperation(
        record.id
      );

      if (res?.ok && res?.data?.status) {
        message.success("Critical operation deleted successfully");
        loadOperations();
      } else {
        message.error(res?.data?.message || "Failed to delete critical operation");
      }
    } catch (error) {
      console.error(error);
      message.error("Delete failed");
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "Operation ID",
        dataIndex: "id",
        key: "id",
        width: 120,
        align: "center",
      },
      {
        title: "Line No",
        dataIndex: "line_no",
        key: "line_no",
        width: 120,
        align: "center",
      },
      {
        title: "Operation",
        dataIndex: "op_name",
        key: "op_name",
        width: 220,
      },
      {
        title: "Device ID",
        dataIndex: "device_id",
        key: "device_id",
        width: 130,
        align: "center",
      },
      {
        title: "Target PCS",
        dataIndex: "target_pcs",
        key: "target_pcs",
        width: 120,
        align: "center",
      },
      {
        title: "Count PCS",
        dataIndex: "count_pcs",
        key: "count_pcs",
        width: 120,
        align: "center",
      },
      {
        title: "Operator",
        dataIndex: "operator_name",
        key: "operator_name",
        width: 180,
      },
      {
        title: "Order No",
        dataIndex: "order_no",
        key: "order_no",
        width: 110,
        align: "center",
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 120,
        align: "center",
        render: (value) =>
          String(value || "").toLowerCase() === "active" ? (
            <span className="sm-pill sm-pill-success">Active</span>
          ) : (
            <span className="sm-pill">{value || "Inactive"}</span>
          ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 140,
        align: "center",
        render: (_, record) => (
          <div className="sm-action-group">
            <Button
              className="sm-icon-btn sm-icon-btn-edit"
              icon={<EditOutlined />}
              onClick={() => handleOpenEdit(record)}
            />
            <Popconfirm
              title="Delete critical operation?"
              description="Are you sure you want to delete this critical operation?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => handleDelete(record)}
            >
              <Button
                className="sm-icon-btn sm-icon-btn-delete"
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="sm-page">
      <div className="sm-page-inner">
        <div className="sm-header">
          <div className="sm-title-wrap">
            <Title level={3} className="sm-page-title">
              <SettingOutlined style={{ color: "#2150FD" }} />
              Critical Operations
            </Title>
            <Text className="sm-page-subtitle">
              Manage all critical operations master records.
            </Text>
          </div>

          <div className="sm-top-actions">
            <Button className="sm-btn-soft" icon={<ReloadOutlined />} onClick={loadOperations}>
              Refresh
            </Button>
            <Button
              className="sm-btn-primary"
              icon={<PlusOutlined />}
              onClick={handleOpenAdd}
            >
              Add New Operation
            </Button>
          </div>
        </div>

        <Card className="sm-card">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={10} lg={8}>
              <label className="sm-label">Search Operation</label>
              <Input
                className="sm-input"
                allowClear
                placeholder="Search..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Col>
          </Row>
        </Card>

        <Card className="sm-card" style={{ marginTop: 16 }}>
          <Spin spinning={loading}>
            {!filteredOperations.length ? (
              <div className="sm-empty-wrap">
                <Empty
                  description={
                    <span className="sm-empty-text">
                      No critical operations found.
                    </span>
                  }
                />
              </div>
            ) : (
              <div className="sm-table-wrap">
                <Table
                  className="sm-table"
                  dataSource={filteredOperations}
                  columns={columns}
                  bordered
                  size="middle"
                  rowKey={(record) => record.key}
                  scroll={{ x: 1400 }}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: false,
                    position: ["bottomCenter"],
                    className: "sm-pagination",
                  }}
                />
              </div>
            )}
          </Spin>
        </Card>

        <Modal
          open={openModal}
          onCancel={handleCloseModal}
          footer={null}
          destroyOnClose
          centered
          width={900}
          title={editingRow ? "Edit Critical Operation" : "Add New Critical Operation"}
        >
          <Form form={form} layout="vertical" onFinish={handleSave}>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Line NO"
                  name="line_no"
                  rules={[{ required: true, message: "Please select line" }]}
                >
                  <Select
                    className="sm-select"
                    placeholder="Select Line"
                    showSearch
                    optionFilterProp="children"
                    loading={mastersLoading}
                    notFoundContent={mastersLoading ? dropdownSpinner : "No data"}
                  >
                    {lines.map((line) => (
                      <Option key={line} value={line}>
                        {line}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Operation"
                  name="op_id"
                  rules={[{ required: true, message: "Please select operation" }]}
                >
                  <Select
                    className="sm-select"
                    placeholder="Select Operation"
                    showSearch
                    optionFilterProp="children"
                    loading={mastersLoading}
                    notFoundContent={mastersLoading ? dropdownSpinner : "No data"}
                  >
                    {tlsOperations.map((op) => (
                      <Option key={op.op_id} value={op.op_id}>
                        {op.op_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Device"
                  name="device_id"
                  rules={[{ required: true, message: "Please select device" }]}
                >
                  <Select
                    className="sm-select"
                    placeholder="Select Device"
                    showSearch
                    optionFilterProp="children"
                    loading={mastersLoading}
                    notFoundContent={mastersLoading ? dropdownSpinner : "No data"}
                  >
                    {devices.map((device, index) => (
                      <Option
                        key={device.id || device.device_id || index}
                        value={device.device_id}
                      >
                        {device.device_id}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Target PCS"
                  name="target_pcs"
                  rules={[{ required: true, message: "Please enter target pcs" }]}
                >
                  <Input className="sm-input" placeholder="Enter Target PCS" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Count PCS"
                  name="count_pcs"
                  rules={[{ required: true, message: "Please select count pcs" }]}
                >
                  <Select className="sm-select" placeholder="Select Count PCS">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                      <Option key={num} value={num}>
                        {num}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Order NO"
                  name="order_no"
                  rules={[{ required: true, message: "Please select order no" }]}
                >
                  <Select className="sm-select" placeholder="Select Order NO">
                    {[1, 2, 3, 4, 5].map((num) => (
                      <Option key={num} value={num}>
                        {num}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Operator"
                  name="operator_id"
                  rules={[{ required: true, message: "Please select operator" }]}
                >
                  <Select
                    className="sm-select"
                    placeholder="Select Operator"
                    showSearch
                    optionFilterProp="children"
                    loading={mastersLoading}
                    notFoundContent={mastersLoading ? dropdownSpinner : "No data"}
                  >
                    {staffUsers.map((user) => (
                      <Option key={user.su_id} value={user.su_id}>
                        {`${(user.full_name || "").trim()} - ${(user.username || "").trim()}`}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item label="Status" name="status" initialValue="active">
                  <Select className="sm-select" placeholder="Select Status">
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <div className="sm-modal-footer">
              <Button className="sm-btn-cancel" onClick={handleCloseModal}>
                Close
              </Button>
              <Button
                className="sm-btn-primary"
                htmlType="submit"
                loading={saving}
              >
                {editingRow ? "Update Operation" : "Save Operation"}
              </Button>
            </div>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default CriticalOperationMaster;